/**
 * Data Normalization Utilities
 *
 * Handles normalization of car data from different sources
 * (Germany, Spain, Portugal) which use different formats and languages.
 *
 * normalizeVersion() lives here (frontend/matching concern).
 * normalizeFuel is re-exported from car-data.js (single source of truth).
 */

import { normalizeFuelForDB } from './car-data.js';

/**
 * Extract the core model identifier from a version/trim string.
 * This extracts engine codes like "320d", "C 200", "S 350" etc.
 * for cross-market matching.
 *
 * @param {string} version - Raw version string
 * @returns {string} Normalized version for matching
 */
export function normalizeVersion(version) {
  if (!version) return 'standard';

  let normalized = version.toString().trim().toLowerCase();

  // Remove common noise words and suffixes
  normalized = normalized
    .replace(/\s*pdk\s*/gi, ' ')
    .replace(/\s*turbo\s*pdk\s*/gi, 'turbo')
    .replace(/\s*macan\s+/gi, '')
    .replace(/\s*e10-enabled\s*/gi, '')
    .replace(/\s*sportpaket\s*/gi, '')
    .replace(/\s*performance\s*package\s*/gi, '')
    .replace(/\b(pack\s*m|m\s*sport|m\s*paket|amg\s*line|avantgarde|elegance|luxury|sport)\b/gi, '')
    .replace(/\b(automático|automático|automatic|manual|aut\.|man\.)\b/gi, '')
    .replace(/\b(cv|hp|ps|kw|bhp)\b/gi, '')
    .replace(/\d{3,}\s*(cv|hp|ps|kw|bhp)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Map common variations
  const versionMap = {
    '.': 'standard',
    '-': 'standard',
    'basis': 'standard',
    '': 'standard',
    's': 's',
    'gts': 'gts',
    '4s': '4s',
    'turbo': 'turbo',
    't': 'turbo'
  };

  if (versionMap[normalized] !== undefined) return versionMap[normalized];

  // Extract core engine/model code (e.g., "320d", "c 200", "s 350 d")
  // Match patterns like: 320d, C200, C 200 d, 520i, A 180, etc.
  const engineMatch = normalized.match(/\b([a-z]?\s*\d{2,3}\s*[a-z]?(?:\s*[a-z])?)\b/i);
  if (engineMatch) {
    return engineMatch[1].replace(/\s+/g, '').toLowerCase();
  }

  return normalized.split(' ')[0] || 'standard';
}

/**
 * Normalize fuel type for comparison (backward-compatible re-export).
 * Delegates to normalizeFuelForDB() from car-data.js.
 */
export const normalizeFuel = normalizeFuelForDB;
